import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { AuthSplitLayout } from '../components/AuthSplitLayout'
import { AuthLoginPreview } from '../components/AuthLoginPreview'
import { OtpInput } from '../components/OtpInput'
import {
  AUTH_BUTTON_CLASS,
  AuthHeader,
  AuthInput,
  AuthLabel,
  FieldError,
  MailIconBadge,
  OrDivider,
  PasswordInput,
  SocialButtons,
  TextButton,
} from '../components/AuthFormParts'
import { authErrorMessage, isCodeError, isUnverifiedError, socialErrorMessage } from '../components/authErrors'
import type { SocialProvider } from '@/lib/auth/authApi'
import { Button } from '@/components/ui/button'
import { getSocialProviders } from '@/lib/auth/authApi'
import { isAuthenticated } from '@/lib/auth/authSession'
import { SocialSignInError, signInWithSocial } from '@/lib/auth/socialAuth'
import {
  requestPasswordReset,
  resendVerificationCode,
  resetPassword,
  signInWithPassword,
  signUp,
  verifyEmail,
} from '@/lib/auth/emailAuth'
import { resolvePostAuth } from '@/lib/auth/postAuthNavigate'
import { isDisposableEmail } from '@/lib/validation/disposableEmailValidator'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_AuthLayout/auth')({
  component: AuthGatewayPage,
})

type AuthView = 'email' | 'password' | 'signup' | 'verify' | 'forgot' | 'reset'
type ErrorField = 'email' | 'password' | 'code'

const CODE_LENGTH = 6

function AuthGatewayPage() {
  const { t } = useTranslation('auth')

  const [view, setView] = useState<AuthView>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [verificationCode, setVerificationCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorField, setErrorField] = useState<ErrorField>('email')
  const [isResending, setIsResending] = useState(false)
  const [resendNotice, setResendNotice] = useState<string | null>(null)

  // Already signed in when the page opened → leave the auth page. Checked once
  // on mount only: a sign-in completing here navigates by itself, and reacting
  // to it would race resolvePostAuth().
  useEffect(() => {
    if (isAuthenticated()) {
      window.location.href = '/'
    }
  }, [])

  // Offline or an older server: no social buttons, email sign-in still works.
  const [socialProviders, setSocialProviders] = useState<Array<SocialProvider>>([])
  useEffect(() => {
    const controller = new AbortController()
    getSocialProviders(controller.signal)
      .then(setSocialProviders)
      .catch(() => setSocialProviders([]))
    return () => controller.abort()
  }, [])

  const showError = (message: string, field: ErrorField) => {
    setError(message)
    setErrorField(field)
  }
  const clearMessages = () => {
    setError(null)
    setResendNotice(null)
  }
  const goTo = (next: AuthView) => {
    clearMessages()
    setView(next)
  }

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault()
    goTo('password')
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()
    setIsLoading(true)

    try {
      await signInWithPassword(email, password)

      const result = await resolvePostAuth()
      window.location.href = result.navigateTo
    } catch (err) {
      if (isUnverifiedError(err)) {
        setView('verify')
        showError(authErrorMessage(err, t, 'errors.signInFailed'), 'code')
      } else {
        showError(authErrorMessage(err, t, 'errors.signInFailed'), 'password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()

    if (await isDisposableEmail(email)) {
      showError(t('errors.disposableEmail'), 'email')
      return
    }

    setIsLoading(true)

    try {
      await signUp(email, password)
      setVerificationCode('')
      setView('verify')
    } catch (err) {
      showError(authErrorMessage(err, t, 'errors.signUpFailed'), 'password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()
    setIsLoading(true)

    try {
      // Verifying the code signs the user in; no separate password sign-in.
      await verifyEmail(email, verificationCode)

      const result = await resolvePostAuth()
      window.location.href = result.navigateTo
    } catch (err) {
      showError(authErrorMessage(err, t, 'errors.verifyFailed'), 'code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    clearMessages()
    setIsResending(true)

    try {
      if (view === 'reset') {
        await requestPasswordReset(email)
      } else {
        await resendVerificationCode(email)
      }
      setResendNotice(t('gateway.code.resent'))
    } catch (err) {
      showError(authErrorMessage(err, t, 'errors.resendFailed'), 'code')
    } finally {
      setIsResending(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()
    setIsLoading(true)

    try {
      await requestPasswordReset(email)
      setView('reset')
    } catch (err) {
      showError(authErrorMessage(err, t, 'errors.forgotFailed'), 'email')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    clearMessages()
    setIsLoading(true)

    try {
      await resetPassword(email, resetCode, newPassword)

      // Auto sign-in with the new password
      await signInWithPassword(email, newPassword)

      const result = await resolvePostAuth()
      window.location.href = result.navigateTo
    } catch (err) {
      showError(authErrorMessage(err, t, 'errors.resetFailed'), isCodeError(err) ? 'code' : 'password')
    } finally {
      setIsLoading(false)
    }
  }

  // Not awaited before window.open: the popup must open inside the click.
  const handleSocialSignIn = (provider: SocialProvider) => {
    clearMessages()
    setIsLoading(true)
    void signInWithSocial(provider)
      .then(async () => {
        const result = await resolvePostAuth()
        window.location.href = result.navigateTo
      })
      .catch((err: unknown) => {
        if (err instanceof SocialSignInError && err.code === 'popup_closed') return
        showError(socialErrorMessage(err, t), 'email')
      })
      .finally(() => setIsLoading(false))
  }

  // Only the providers moly-api has credentials for; none, and the buttons stay hidden.
  const socialSignIn =
    socialProviders.length > 0 ? (
      <>
        <SocialButtons providers={socialProviders} onSignIn={handleSocialSignIn} disabled={isLoading} />
        <OrDivider />
      </>
    ) : null

  const switchToForgot = () => {
    goTo('forgot')
    setResetCode('')
    setNewPassword('')
  }

  const switchToEmail = () => {
    goTo('email')
    setPassword('')
    setVerificationCode('')
  }

  const fieldError = (field: ErrorField) => (error && errorField === field ? error : null)
  const errorId = (field: ErrorField) => `auth-${field}-error`

  const submitLabel = (label: string, withArrow = false) =>
    isLoading ? (
      <Loader2 className="size-4 animate-spin" />
    ) : (
      <>
        {label}
        {withArrow ? <ArrowRight className="size-4" /> : null}
      </>
    )

  const emailField = (id: string, autoFocus: boolean, label = t('gateway.email.label')) => (
    <div className="grid gap-2">
      <AuthLabel htmlFor={id}>{label}</AuthLabel>
      <AuthInput
        id={id}
        type="email"
        placeholder={t('gateway.email.placeholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
        autoFocus={autoFocus}
        aria-invalid={Boolean(fieldError('email')) || undefined}
        aria-describedby={fieldError('email') ? errorId('email') : undefined}
      />
      {fieldError('email') ? <FieldError id={errorId('email')}>{fieldError('email')}</FieldError> : null}
    </div>
  )

  const codeResend = (
    <p className="text-sm text-muted-foreground">
      {t('gateway.code.didntGet')}{' '}
      <TextButton onClick={handleResendCode} disabled={isResending || isLoading}>
        {isResending ? t('gateway.code.resending') : t('gateway.code.resend')}
      </TextButton>
    </p>
  )

  const codeHeader = (
    <AuthHeader
      icon={<MailIconBadge />}
      title={t('gateway.code.title')}
      subtitle={
        <Trans
          t={t}
          i18nKey="gateway.code.sentTo"
          values={{ email }}
          components={{ b: <strong className="font-medium text-foreground" /> }}
        />
      }
    />
  )

  const codeField = (value: string, onChange: (v: string) => void) => (
    <div className="grid gap-2">
      <OtpInput
        id="auth-code"
        value={value}
        onChange={onChange}
        length={CODE_LENGTH}
        autoFocus
        invalid={Boolean(fieldError('code'))}
        describedBy={fieldError('code') ? errorId('code') : undefined}
      />
      {fieldError('code') ? <FieldError id={errorId('code')}>{fieldError('code')}</FieldError> : null}
      {resendNotice && !error ? (
        <p role="status" className="text-sm text-muted-foreground">{resendNotice}</p>
      ) : null}
    </div>
  )

  return (
    <AuthSplitLayout preview={<AuthLoginPreview />}>
      {view === 'email' && (
        <div className="space-y-7">
          <AuthHeader title={t('gateway.email.title')} subtitle={t('gateway.email.subtitle')} />

          <div className="space-y-5">
            {socialSignIn}
            <form onSubmit={handleEmailContinue} className="grid gap-4">
              {emailField('email', true)}
              <Button type="submit" className={cn(AUTH_BUTTON_CLASS, 'w-full')}>
                {t('gateway.email.submit')}
                <ArrowRight className="size-4" />
              </Button>
            </form>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('gateway.email.newToMoly')}{' '}
            <TextButton onClick={() => goTo('signup')}>{t('gateway.email.createWorkspace')}</TextButton>
          </p>
        </div>
      )}

      {view === 'password' && (
        <div className="space-y-7">
          <AuthHeader title={t('gateway.email.title')} subtitle={t('gateway.email.subtitle')} />

          <form onSubmit={handleSignIn} className="grid gap-4">
            <div className="flex items-center gap-2.5 rounded-lg bg-secondary px-3 py-2.5">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-semibold uppercase text-brand-foreground"
              >
                {email.trim().charAt(0) || '?'}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{email}</span>
              <TextButton className="text-sm text-muted-foreground hover:text-foreground" onClick={switchToEmail}>
                {t('gateway.password.change')}
              </TextButton>
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <AuthLabel htmlFor="password">{t('gateway.password.label')}</AuthLabel>
                <TextButton className="text-sm text-muted-foreground hover:text-foreground" onClick={switchToForgot}>
                  {t('gateway.password.forgot')}
                </TextButton>
              </div>
              <PasswordInput
                id="password"
                placeholder={t('gateway.password.placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                autoFocus
                aria-invalid={Boolean(fieldError('password')) || undefined}
                aria-describedby={fieldError('password') ? errorId('password') : undefined}
              />
              {fieldError('password') ? (
                <FieldError id={errorId('password')}>{fieldError('password')}</FieldError>
              ) : null}
            </div>

            {/* Hidden username keeps password managers pairing the right account. */}
            <input type="email" value={email} autoComplete="username" readOnly hidden />

            <Button type="submit" disabled={isLoading} className={cn(AUTH_BUTTON_CLASS, 'w-full')}>
              {submitLabel(t('gateway.password.submit'))}
            </Button>
          </form>
        </div>
      )}

      {view === 'signup' && (
        <div className="space-y-7">
          <AuthHeader title={t('gateway.signup.title')} subtitle={t('gateway.signup.subtitle')} />

          <div className="space-y-5">
            {socialSignIn}
            <form onSubmit={handleSignUp} className="grid gap-4">
              {emailField('signup-email', true, t('gateway.signup.emailLabel'))}

              <div className="grid gap-2">
                <AuthLabel htmlFor="signup-password">{t('gateway.signup.passwordLabel')}</AuthLabel>
                <PasswordInput
                  id="signup-password"
                  placeholder={t('gateway.signup.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  aria-invalid={Boolean(fieldError('password')) || undefined}
                  aria-describedby={fieldError('password') ? errorId('password') : 'signup-password-hint'}
                />
                {fieldError('password') ? (
                  <FieldError id={errorId('password')}>{fieldError('password')}</FieldError>
                ) : (
                  <p id="signup-password-hint" className="text-xs text-muted-foreground">
                    {t('gateway.signup.passwordHint')}
                  </p>
                )}
              </div>

              <Button type="submit" disabled={isLoading} className={cn(AUTH_BUTTON_CLASS, 'w-full')}>
                {submitLabel(t('gateway.signup.submit'), true)}
              </Button>
            </form>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('gateway.signup.haveAccount')}{' '}
            <TextButton onClick={switchToEmail}>{t('gateway.signup.login')}</TextButton>
          </p>
        </div>
      )}

      {view === 'verify' && (
        <div className="space-y-7">
          {codeHeader}

          <form onSubmit={handleVerify} className="grid gap-4">
            {codeField(verificationCode, setVerificationCode)}
            <Button
              type="submit"
              disabled={isLoading || verificationCode.length < CODE_LENGTH}
              className={cn(AUTH_BUTTON_CLASS, 'w-full')}
            >
              {submitLabel(t('gateway.verify.submit'))}
            </Button>
          </form>

          <div className="space-y-2">
            {codeResend}
            <TextButton className="text-sm text-muted-foreground hover:text-foreground" onClick={switchToEmail}>
              {t('gateway.code.back')}
            </TextButton>
          </div>
        </div>
      )}

      {view === 'forgot' && (
        <div className="space-y-7">
          <AuthHeader title={t('gateway.forgot.title')} subtitle={t('gateway.forgot.subtitle')} />

          <form onSubmit={handleForgotPassword} className="grid gap-4">
            {emailField('forgot-email', true)}
            <Button type="submit" disabled={isLoading} className={cn(AUTH_BUTTON_CLASS, 'w-full')}>
              {submitLabel(t('gateway.forgot.submit'))}
            </Button>
          </form>

          <TextButton className="text-sm text-muted-foreground hover:text-foreground" onClick={switchToEmail}>
            {t('gateway.forgot.back')}
          </TextButton>
        </div>
      )}

      {view === 'reset' && (
        <div className="space-y-7">
          {codeHeader}

          <form onSubmit={handleResetPassword} className="grid gap-4">
            {codeField(resetCode, setResetCode)}

            <div className="grid gap-2">
              <AuthLabel htmlFor="new-password">{t('gateway.reset.newPassword')}</AuthLabel>
              <PasswordInput
                id="new-password"
                placeholder={t('gateway.reset.newPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                aria-invalid={Boolean(fieldError('password')) || undefined}
                aria-describedby={fieldError('password') ? errorId('password') : 'new-password-hint'}
              />
              {fieldError('password') ? (
                <FieldError id={errorId('password')}>{fieldError('password')}</FieldError>
              ) : (
                <p id="new-password-hint" className="text-xs text-muted-foreground">
                  {t('gateway.signup.passwordHint')}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isLoading || resetCode.length < CODE_LENGTH}
              className={cn(AUTH_BUTTON_CLASS, 'w-full')}
            >
              {submitLabel(t('gateway.reset.submit'))}
            </Button>
          </form>

          <div className="space-y-2">
            {codeResend}
            <TextButton className="text-sm text-muted-foreground hover:text-foreground" onClick={switchToEmail}>
              {t('gateway.code.back')}
            </TextButton>
          </div>
        </div>
      )}
    </AuthSplitLayout>
  )
}
